/**
 * CONNECTIONS (PODIUM-CONNECT-BUTTON, review 2026-11-02 P0 #8 follow-up).
 *
 * Settings → System → Connections: the outside services Pegasus is wired to,
 * each with its live state and — where the service hands out tokens through a
 * browser flow — the button that (re)connects it. First row: Podium (texting).
 * Microsoft 365, Telnyx and Stripe hold their keys in the server vault; they
 * appear here as the Health card grows (SETTINGS-REGISTRY-01).
 *
 * The Podium flow: an admin taps Reconnect → `podium_oauth_begin()` mints a
 * one-time `state` and returns the authorize URL → a new tab opens on Podium →
 * Podium sends the browser back to the callback function, which accepts the
 * code only with that fresh state and stores the token. Nothing here ever sees
 * the token; `podium_connection_status()` returns connected / refreshed /
 * expires / webhook / mapped users and nothing else.
 *
 * Competitor frame: HubSpot "Connected apps" (one page, one row per service,
 * state + reconnect) — adopted; ServiceTitan "Integrations" marketplace page —
 * same shape, too heavy for four services; Jobber "Apps" — a store, not a status
 * page. Rejected: showing token fragments or scopes as the headline (support
 * trivia; the scope list sits under the (i)).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Link2, MessageSquareText, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { Button } from "@/components/ui/Button";
import { usePermissions } from "@/lib/permissions/PermissionProvider";

interface PodiumStatus {
  connected: boolean;
  expires_at?: string | null;
  updated_at?: string | null;
  scope?: string | null;
  has_webhook?: boolean;
  locations?: number;
}

function usePodiumStatus(poll: boolean) {
  return useQuery({
    queryKey: ["podium_connection_status"],
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: poll ? 5000 : false,
    queryFn: async (): Promise<PodiumStatus> => {
      const { data, error } = await supabase.rpc("podium_connection_status");
      if (error) throw error;
      return (data ?? { connected: false, locations: 0 }) as PodiumStatus;
    },
  });
}

const fmt = (iso: string | null | undefined, withTime = true) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "numeric", minute: "2-digit" } : {}),
  });
};

export function ConnectionsSection() {
  const { isAdmin } = usePermissions();
  const [polling, setPolling] = useState(false);
  const [minting, setMinting] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const status = usePodiumStatus(polling);
  const s = status.data;
  const startedFrom = useRef<string | null>(null);
  const pollTimer = useRef<number | null>(null);

  // While a connect is in flight, poll until the token row changes (or 3 min pass).
  useEffect(() => {
    if (!polling) return;
    if (s?.updated_at && startedFrom.current !== null && s.updated_at !== startedFrom.current) {
      setPolling(false);
      setFallbackUrl(null);
    }
  }, [polling, s?.updated_at]);
  useEffect(() => {
    if (!polling) return;
    pollTimer.current = window.setTimeout(() => setPolling(false), 3 * 60 * 1000);
    return () => {
      if (pollTimer.current) window.clearTimeout(pollTimer.current);
    };
  }, [polling]);

  async function connect() {
    setError(null);
    setMinting(true);
    // Open the tab synchronously so the browser treats it as the click's own window
    // (a tab opened after the await is what pop-up blockers stop).
    const w = window.open("about:blank", "_blank");
    try {
      const { data, error: e } = await supabase.rpc("podium_oauth_begin");
      if (e) throw e;
      const url = String(data ?? "");
      if (!/^https:\/\/api\.podium\.com\/oauth\/authorize\?/.test(url)) throw new Error("Unexpected connect link.");
      startedFrom.current = s?.updated_at ?? null;
      setPolling(true);
      if (w) {
        w.opener = null;
        w.location.href = url;
      } else {
        setFallbackUrl(url);
      }
    } catch (err) {
      if (w) w.close();
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setMinting(false);
    }
  }

  const expired = Boolean(s?.expires_at && new Date(s.expires_at).getTime() < Date.now());
  const healthy = Boolean(s?.connected) && !expired;

  return (
    <div className="rounded-xl border border-hairline bg-white shadow-e1">
      <ul className="divide-y divide-hairline">
        <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600">
            <MessageSquareText className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-semibold text-slate-900">Podium</span>
              <InfoTip label="About the Podium connection">
                Texting runs through Podium: inbound texts land in the Inbox, and automations, reminders and replies go out
                through it. Podium hands Pegasus a token that refreshes itself every few hours; if that chain ever breaks
                (a password change at Podium, a revoked app), texts stop and this row shows Not Connected — Reconnect
                signs in again as the Sedgwick Podium admin and nothing else changes.
                {s?.scope ? ` Granted: ${s.scope.split(" ").join(", ")}.` : ""}
              </InfoTip>
            </div>
            <div className="mt-0.5 truncate text-[12px] text-slate-500">
              {status.isLoading
                ? "…"
                : status.isError
                  ? "Status unavailable"
                  : s?.connected
                    ? [
                        s.updated_at ? `Refreshed ${fmt(s.updated_at)}` : null,
                        s.expires_at ? `${expired ? "Expired" : "Expires"} ${fmt(s.expires_at)}` : null,
                        s.has_webhook ? "Webhook On" : "No Webhook",
                        typeof s.locations === "number" ? `${s.locations} Users Mapped` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "No token on file"}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold",
                healthy ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
              )}
            >
              {status.isLoading ? "…" : healthy ? "Connected" : expired ? "Token Expired" : "Not Connected"}
            </span>
            {isAdmin &&
              (fallbackUrl ? (
                <a
                  href={fallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-8 items-center gap-1.5 rounded-full bg-brand-700 px-3 text-xs font-semibold text-white hover:bg-brand-800"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open Podium
                </a>
              ) : (
                <Button size="sm" variant={healthy ? "secondary" : "primary"} loading={minting} onClick={() => void connect()}>
                  {polling ? (
                    <>
                      <RefreshCw className="mr-1 h-3.5 w-3.5 animate-spin" /> Waiting for Podium
                    </>
                  ) : (
                    <>
                      <Link2 className="mr-1 h-3.5 w-3.5" /> {healthy ? "Reconnect" : "Connect"}
                    </>
                  )}
                </Button>
              ))}
          </div>
          {error && <div className="basis-full text-[12.5px] text-red-600">{error}</div>}
        </li>
      </ul>
    </div>
  );
}
